import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import {
  useCreateCategory,
  useSetCategoryActive,
  useUpdateCategory,
} from '@/hooks/property-categories';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import type { PropertyCategoryView } from '@shared/schemas/catalog';

/**
 * Create or edit a property category.
 *
 * One dialog for both, because they are the same fields and a second component
 * is a second place for one of them to be forgotten. `category` absent means
 * create.
 *
 * The form lives in a child component so that **Radix unmounting the closed
 * dialog is what resets it**. Holding the field state out here would mean it
 * survives a cancel, and the next person to open the dialog would find whatever
 * the last one typed — the usual fix for which is an effect that copies props
 * into state, which is a render cascade and a lint error and still one edge case
 * away from wrong.
 */
export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: PropertyCategoryView;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <CategoryForm
            category={category}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CategoryForm({
  category,
  onDone,
}: {
  category?: PropertyCategoryView;
  onDone: () => void;
}) {
  const toast = useToast();
  const isEdit = category !== undefined;

  // Initialised from props at mount, which is the only time this component
  // mounts — see the note above.
  const [name, setName] = useState(category?.name ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [needsReview, setNeedsReview] = useState(category?.needsReview ?? false);
  const [reviewNote, setReviewNote] = useState(category?.reviewNote ?? '');

  const create = useCreateCategory();
  const update = useUpdateCategory(category?.id ?? '');
  const setActive = useSetCategoryActive(category?.id ?? '');
  const mutation = isEdit ? update : create;

  const error = mutation.error instanceof ApiError ? mutation.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();

    mutation.mutate(
      {
        name,
        description,
        needsReview,
        // Clearing the flag clears its note too, so a category cannot carry a
        // reason for a review it is no longer waiting on.
        reviewNote: needsReview ? reviewNote : '',
      },
      {
        onSuccess: (saved) => {
          toast.success(isEdit ? `Saved ${saved.name}.` : `Added ${saved.name}.`);
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">
        {isEdit ? `Edit ${category.name}` : 'Add a property category'}
      </Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        A category is a type of property. Its checklist is what a cleaner works through on a job.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {/* A whole-form failure. A field-level one is rendered under its field
            instead, so the same message is never in two places. */}
        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <Field
          label="Name"
          name="name"
          required
          value={name}
          error={fieldErrors.name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          placeholder="Office Building"
        />

        <div className="space-y-1.5">
          <Label htmlFor="category-description">Description</Label>
          <textarea
            id="category-description"
            name="description"
            rows={3}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body placeholder:text-ink-500"
            placeholder="What kind of property this covers."
          />
        </div>

        <div className="rounded-sm border border-ink-300 bg-ink-100/40 p-3">
          <label className="flex min-h-touch items-center gap-2.5">
            <input
              type="checkbox"
              name="needsReview"
              checked={needsReview}
              onChange={(event) => {
                setNeedsReview(event.target.checked);
              }}
              className="size-4 shrink-0 accent-honey-500"
            />
            <span className="text-label text-ink-900">Needs review</span>
          </label>
          <p className="mt-1 text-meta text-ink-500">
            Mark a category whose checklist has not been written yet, so an empty list is not
            mistaken for a finished one.
          </p>

          {needsReview ? (
            <div className="mt-3">
              <Label htmlFor="category-review-note">What needs deciding</Label>
              <textarea
                id="category-review-note"
                name="reviewNote"
                rows={2}
                value={reviewNote}
                onChange={(event) => {
                  setReviewNote(event.target.value);
                }}
                className="mt-1.5 w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
              />
            </div>
          ) : null}
        </div>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          {isEdit ? (
            <Button
              variant="outline"
              className="sm:mr-auto"
              disabled={setActive.isPending}
              onClick={() => {
                setActive.mutate(!category.isActive, {
                  onSuccess: (saved) => {
                    toast.success(
                      saved.isActive ? `${saved.name} is active again.` : `${saved.name} retired.`,
                    );
                    onDone();
                  },
                  onError: (cause) => {
                    toast.error(
                      cause instanceof ApiError ? cause.message : 'That could not be saved.',
                    );
                  },
                });
              }}
            >
              {category.isActive ? 'Retire this category' : 'Bring it back'}
            </Button>
          ) : null}

          <Dialog.Close asChild>
            <Button variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add category'}
          </Button>
        </div>
      </form>
    </>
  );
}
